// Assignment-level compensation (canonical).
//
// An instructor's pay belongs to the instructor + class/session ASSIGNMENT,
// not to the person. The same instructor may simultaneously be paid the Hub
// standard rate for one class, a custom per-meeting rate for another, and
// volunteer ($0) for a third.
//
// This module is the single source of truth for how an assignment's
// compensation is LABELLED and PRICED in the admin UI. The database function
// `resolve_instructor_compensation` implements the identical rules for
// payroll, so the card and the paycheck can never disagree.
import { supabase } from "@/integrations/supabase/client";

export type CompensationMode = "hub_standard" | "custom_per_meeting" | "volunteer";

export const COMPENSATION_MODE_LABEL: Record<CompensationMode, string> = {
  hub_standard: "Standard Hub Rate",
  custom_per_meeting: "Custom",
  volunteer: "Volunteer",
};

/** Hub schedule — mirrors public.payroll_standard_rates. */
export const HUB_STANDARD_RATE_CENTS: Record<number, number> = {
  60: 3000,
  90: 3500,
  120: 4000,
};

/** Minutes between two clock strings ("10:00 AM", "11:30"), or null. */
export function parseClockMinutes(v: string | null | undefined): number | null {
  if (!v) return null;
  const m = String(v).trim().match(/^(\d{1,2}):?(\d{2})?\s*([ap]\.?m\.?)?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  const ap = (m[3] ?? "").toLowerCase();
  if (ap.startsWith("p") && h < 12) h += 12;
  if (ap.startsWith("a") && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * Canonical meeting duration for an assignment. Mirrors `inferDuration` in
 * supabase/functions/_shared/payroll-schedule.ts so the UI preview and the
 * payroll engine always infer the same number of minutes.
 */
export function canonicalDurationMinutes(a: {
  start_time?: string | null;
  end_time?: string | null;
}): number | null {
  const s = parseClockMinutes(a?.start_time);
  let e = parseClockMinutes(a?.end_time);
  if (s == null || e == null) return null;
  if (e <= s) e += 12 * 60;
  const diff = e - s;
  return diff > 0 && diff <= 12 * 60 ? diff : null;
}

/** Hub rate for a duration, or null when the duration is unknown/off-schedule. */
export function hubRateCents(durationMinutes: number | null): number | null {
  if (durationMinutes == null) return null;
  return HUB_STANDARD_RATE_CENTS[durationMinutes] ?? null;
}

export function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

export interface AssignmentCompensationInput {
  compensation_mode?: string | null;
  compensation_rate_cents?: number | null;
  compensation_effective_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}

/**
 * A compensation_config row that payroll would fall back to for this
 * instructor/class when the assignment itself carries no mode. Supplying it
 * lets the card show the rate payroll ACTUALLY resolves instead of
 * "Compensation not set".
 */
export interface CompensationConfigFallback {
  compensation_source?: string | null;
  rate_cents?: number | null;
  rate_model?: string | null;
  /** True when the config is instructor-wide (no class attached). */
  instructorWide?: boolean;
}

export interface ResolvedAssignmentCompensation {
  mode: CompensationMode | null;
  /** Per-meeting amount payroll will use, or null when it needs review. */
  rateCents: number | null;
  durationMinutes: number | null;
  /** True when payroll cannot safely price this assignment yet. */
  needsReview: boolean;
  /** Where the rate comes from: "Assignment-specific", "Hub Standard", … */
  sourceLabel: string;
  /** Card text payroll agrees with. */
  label: string;
  effectiveDate: string | null;
}

const NEEDS_REVIEW_LABEL = "⚠ Needs Review — compensation cannot be determined";

/**
 * Exactly what payroll will pay for one meeting of this assignment — computed
 * from the SAME precedence the database resolver uses:
 * assignment/session → instructor+class config → instructor-wide config →
 * Hub standard by duration → Needs Review.
 */
export function resolveAssignmentCompensation(
  a: AssignmentCompensationInput,
  fallback?: CompensationConfigFallback | null,
): ResolvedAssignmentCompensation {
  const durationMinutes = canonicalDurationMinutes(a);
  const effectiveDate = a.compensation_effective_date ?? null;
  const mode = (a.compensation_mode ?? null) as CompensationMode | null;
  const mins = durationMinutes == null ? null : `${durationMinutes} minutes`;

  const base = { mode, durationMinutes, effectiveDate };

  if (mode === "volunteer") {
    return { ...base, rateCents: 0, needsReview: false, sourceLabel: "Volunteer", label: "Volunteer · $0" };
  }

  if (mode === "custom_per_meeting") {
    const cents = a.compensation_rate_cents ?? null;
    return {
      ...base,
      rateCents: cents,
      needsReview: cents == null,
      sourceLabel: "Assignment-specific",
      label: cents == null ? NEEDS_REVIEW_LABEL : `${formatUsd(cents)}/meeting · Assignment-specific`,
    };
  }

  if (mode === "hub_standard") {
    const cents = hubRateCents(durationMinutes);
    return {
      ...base,
      rateCents: cents,
      needsReview: cents == null,
      sourceLabel: "Hub Standard",
      label: cents == null ? NEEDS_REVIEW_LABEL : `${formatUsd(cents)}/meeting · Hub Standard · ${mins}`,
    };
  }

  // No assignment-level setting — mirror the resolver's fallback chain.
  if (fallback) {
    const scope = fallback.instructorWide ? "Instructor-wide rate" : "Class-specific rate";
    if (fallback.compensation_source === "volunteer") {
      return { ...base, rateCents: 0, needsReview: false, sourceLabel: scope, label: `Volunteer · $0 · ${scope}` };
    }
    if (fallback.rate_model === "enrollment_tiered") {
      return {
        ...base,
        rateCents: fallback.rate_cents ?? null,
        needsReview: false,
        sourceLabel: scope,
        label: `Enrollment-tiered · ${scope}`,
      };
    }
    if (fallback.rate_cents != null) {
      return {
        ...base,
        rateCents: fallback.rate_cents,
        needsReview: false,
        sourceLabel: scope,
        label: `${formatUsd(fallback.rate_cents)}/meeting · ${scope}`,
      };
    }
  }

  const hub = hubRateCents(durationMinutes);
  if (hub != null) {
    return {
      ...base,
      rateCents: hub,
      needsReview: false,
      sourceLabel: "Hub Standard",
      label: `${formatUsd(hub)}/meeting · Hub Standard · ${mins}`,
    };
  }

  return { ...base, rateCents: null, needsReview: true, sourceLabel: "Needs Review", label: NEEDS_REVIEW_LABEL };
}

/** True when this assignment creates no payable payroll obligation. */
export function isVolunteerAssignment(a: AssignmentCompensationInput): boolean {
  return (a.compensation_mode ?? null) === "volunteer";
}

export interface SaveCompensationInput {
  assignmentId: string;
  mode: CompensationMode;
  /** Required for custom_per_meeting. */
  rateCents?: number | null;
  effectiveDate?: string | null;
  notes?: string | null;
}

/**
 * Writes the assignment's compensation. Effective-dated: payroll prices each
 * meeting on its own date, so only meetings on/after the effective date that
 * have NOT already been paid can change. Paid ledger entries, confirmed
 * payroll and closed periods are never touched here.
 */
export async function saveAssignmentCompensation(input: SaveCompensationInput): Promise<void> {
  if (input.mode === "custom_per_meeting" && (input.rateCents == null || input.rateCents < 0)) {
    throw new Error("Enter the dollar amount for this custom per-meeting rate.");
  }
  const patch: Record<string, unknown> = {
    compensation_mode: input.mode,
    compensation_rate_cents:
      input.mode === "custom_per_meeting"
        ? input.rateCents
        : input.mode === "volunteer"
          ? 0
          : null,
    compensation_effective_date: input.effectiveDate ?? null,
  };
  if (input.notes !== undefined) patch.compensation_notes = input.notes;

  const { error } = await (supabase.from as any)("instructor_class_assignments")
    .update(patch)
    .eq("id", input.assignmentId);
  if (error) throw error;
}
